import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { UserContext } from "../contexts/UserContext";
import "./ChatBox.css";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";
const STORAGE_KEY = "twinpack_chat_history";
const MAX_HISTORY  = 30; // mensajes a conservar en localStorage

// ─── Mini renderer de markdown ────────────────────────────────────────────────
// Soporta **negrita**, *cursiva* y `codigo`. No usa innerHTML (safe).
const renderMarkdown = (text) => {
  if (!text) return null;
  const regex = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;
  const parts = [];
  let lastIndex = 0;
  let key = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const t = match[0];
    if (t.startsWith("**")) {
      parts.push(<strong key={key++}>{t.slice(2, -2)}</strong>);
    } else if (t.startsWith("`")) {
      parts.push(
        <code key={key++} style={{
          background: "rgba(0,0,0,0.06)", padding: "1px 5px",
          borderRadius: 4, fontSize: "0.92em", fontFamily: "monospace",
        }}>{t.slice(1, -1)}</code>
      );
    } else if (t.startsWith("*")) {
      parts.push(<em key={key++}>{t.slice(1, -1)}</em>);
    }
    lastIndex = match.index + t.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

// ─── Ícono enviar (flecha) ────────────────────────────────────────────────────
const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

// ─── Ícono chat ───────────────────────────────────────────────────────────────
const ChatIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

// ─── Componente principal ─────────────────────────────────────────────────────
function ChatBox() {
  const history = useHistory();
  const { user } = useContext(UserContext);

  // Obtener userId desde contexto o localStorage
  const userId = user?.id
    || user?.datos?.id
    || (() => {
         try { return JSON.parse(localStorage.getItem("user") || "{}").id; }
         catch { return 0; }
       })();

  const [open,     setOpen]     = useState(false);
  const [input,    setInput]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  // Auto-scroll al último mensaje
  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, loading]);

  // Foco en input al abrir
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  // Persistir historial
  const persist = useCallback((msgs) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(msgs.slice(-MAX_HISTORY)));
    } catch {}
  }, []);

  const handleClear = () => {
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: "user", content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${BASE}/chat.php`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          userId,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Navegar si el asistente lo pidió
      if (data.navigate) {
        history.push(data.navigate);
      }

      const assistantText = data.response || data.error || "No pude responder. Intentá de nuevo.";
      const assistantMsg  = { role: "assistant", content: assistantText };
      const updated       = [...newMessages, assistantMsg];
      setMessages(updated);
      persist(updated);
    } catch (err) {
      const errMsg = { role: "assistant", content: "Error de conexión. Verificá que el servidor esté activo." };
      const updated = [...newMessages, errMsg];
      setMessages(updated);
      persist(updated);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* ── FAB ── */}
      <button
        className="chatbox-fab"
        onClick={() => setOpen(o => !o)}
        title={open ? "Cerrar asistente" : "Abrir asistente IA"}
        aria-label="Asistente IA"
      >
        {open ? "✕" : <ChatIcon />}
      </button>

      {/* ── Panel ── */}
      {open && (
        <div className="chatbox-panel">
          {/* Header */}
          <div className="chatbox-header">
            <div className="chatbox-header-title">
              <span /> Asistente Twinpack
            </div>
            <div className="chatbox-header-actions">
              {messages.length > 0 && (
                <button className="chatbox-header-btn" onClick={handleClear} title="Limpiar historial">
                  Limpiar
                </button>
              )}
            </div>
          </div>

          {/* Mensajes */}
          <div className="chatbox-messages">
            {messages.length === 0 ? (
              <div className="chatbox-empty">
                <div className="chatbox-empty-icon">🤖</div>
                <div>
                  <strong>¿En qué te ayudo?</strong><br />
                  <span style={{ fontSize: 12 }}>
                    Podés preguntarme sobre órdenes, clientes, tareas o pedirme que te lleve a una sección.
                  </span>
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div key={idx} className={`chatbox-msg ${msg.role}`}>
                  <div className="chatbox-bubble">
                    {msg.role === "assistant" ? renderMarkdown(msg.content) : msg.content}
                  </div>
                </div>
              ))
            )}

            {/* Typing indicator */}
            {loading && (
              <div className="chatbox-typing">
                <span /><span /><span />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chatbox-footer">
            <textarea
              ref={inputRef}
              className="chatbox-input"
              placeholder="Escribí tu consulta…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={loading}
            />
            <button
              className="chatbox-send"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              title="Enviar"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ChatBox;
